import type { GridApi } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import type { DatasetPage } from '@/common/hooks/useDatasetPages';
import { commonQueryKeys } from '@/common/queryKeys/common';
import type { Schedule } from '@/features/settings/work-integrations/api/schedules';
import { SchedulesApi } from '@/features/settings/work-integrations/api/schedules';
import { workIntegrationsQueryKeys } from '@/features/settings/work-integrations/queryKeys';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import { useCrossCachePagesSync } from '../hooks/useCrossCachePagesSync';
import { useTablePagedQuery } from '../hooks/useTablePagedQuery';

const EMPTY_SCHEDULES: ReadonlyMap<number, Schedule | null> = new Map();
const matchScheduleKey = buildIdKeyMatcher(workIntegrationsQueryKeys.scheduleByItemId);
const scheduleFromSource = (_id: number, sourceData: unknown): Schedule | null | undefined =>
  (sourceData ?? null) as Schedule | null;

type VisibleSchedulesContextType = {
  scheduleById: ReadonlyMap<number, Schedule | null>;
};

const VisibleSchedulesContext = React.createContext<VisibleSchedulesContextType | undefined>(
  undefined,
);

export const useVisibleSchedule = (id: number | undefined): Schedule | null | undefined => {
  const context = React.useContext(VisibleSchedulesContext);
  if (!context || id === undefined) return undefined;
  return context.scheduleById.get(id);
};

export const VisibleSchedulesProvider = ({
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const fetchByIds = React.useCallback(
    async (ids: readonly number[]) => {
      const response = await SchedulesApi.getSchedulesByItemIds(new Set(ids));
      const scheduleById = new Map<number, Schedule | null>();
      for (const id of ids) {
        const schedule = response[id] ?? null;
        scheduleById.set(id, schedule);
        queryClient.setQueryData(workIntegrationsQueryKeys.scheduleByItemId(id), schedule);
      }
      return scheduleById;
    },
    [queryClient],
  );

  const { loadedById } = useTablePagedQuery<Schedule | null>({
    providerName: commonQueryKeys.itemsTable.visibleSchedules,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled,
    apiRef,
  });

  useCrossCachePagesSync<Schedule | null>({
    providerName: commonQueryKeys.itemsTable.visibleSchedules,
    instanceKey,
    pageIndexById,
    datasetSignature,
    matchKey: matchScheduleKey,
    transform: scheduleFromSource,
  });

  const value = React.useMemo<VisibleSchedulesContextType>(
    () => ({ scheduleById: loadedById.size > 0 ? loadedById : EMPTY_SCHEDULES }),
    [loadedById],
  );

  return (
    <VisibleSchedulesContext.Provider value={value}>{children}</VisibleSchedulesContext.Provider>
  );
};
